import React from "react";
import { Navigate, Outlet, ScrollRestoration } from "react-router-dom";
import useFragmentLanding from "./fragment-landing";
import BackToTop from "components/BackToTop/BackToTop.js";
import ScrollProgress from "motion/ScrollProgress.js";

import LandingPage from "views/Landing/landing.js";
import ServicePage from "views/Services/ServicePage.js";
import GatedResources from "views/Resources/GatedResources.js";
import { SERVICES, HIDDEN_SERVICES } from "views/Services/services-config.js";
import GatedService from "views/Services/GatedService.js";

// Scroll position belongs to the router, not to each page. Left to itself the
// browser restores it during the history navigation, before React has rendered
// the incoming route — so there is not yet enough document to scroll through
// and the restore quietly lands short. <ScrollRestoration> takes it off the
// browser (`history.scrollRestoration = "manual"`), records where the reader
// was under each history entry, and puts them back there once the new page has
// been laid out: saved position first, then the URL's fragment, then the top.
function Root() {
  // Corrects the one case <ScrollRestoration> gets wrong; see fragment-landing.js.
  useFragmentLanding();
  return (
    <>
      <ScrollRestoration />
      {/* Where the reader is in the page, on the same reasoning as the button
          below: chrome the whole site wears, not a feature of the long pages
          that happen to want it most. Pinning it under the service pages' jump
          nav would have given some pages a bar and others none, which is the
          quirk that note is about. First in the tree because it is fixed to the
          top of the window and, being aria-hidden, has no place in the tab order
          to compete for. */}
      <ScrollProgress />
      <Outlet />
      {/* Getting back is scroll chrome, like the restoration above, so it lives
          with it rather than being repeated by every page. Rendered last so it
          falls at the end of the tab order, where a control that is only wanted
          after a long read belongs. */}
      <BackToTop />
    </>
  );
}

// A rendered <Navigate>, not a loader returning `redirect()`. The router decides
// whether a loader's return is a redirect by duck-typing it — among other things
// it requires `typeof value.body !== "undefined"` — and the Response that jsdom
// supplies fails that test, so under jest a loader redirect is discarded in
// silence. There is no data being loaded here to justify a path the tests cannot
// reach.
const toLanding = <Navigate to="/index" replace />;

export const routes = [
  {
    element: <Root />,
    children: [
      { path: "/index", element: <LandingPage /> },
      { path: "/services", element: toLanding },
      ...SERVICES.map((s) => ({
        path: `/${s.slug}`,
        element: <ServicePage serviceKey={s.key} />,
      })),
      // Unlisted pages. They resolve like any other service page, but nothing
      // links to them and each sits behind a soft gate. See HIDDEN_SERVICES.
      ...HIDDEN_SERVICES.map((s) => ({
        path: `/${s.slug}`,
        element: <GatedService service={s} />,
      })),
      // Gated while unpublished, the same way the unlisted service pages are.
      // The route stays live so the page can be reviewed at its real address
      // rather than only in a dev server.
      { path: "/resources", element: <GatedResources /> },
      // The motion lab, in development only. Deliberately `require` inside the
      // branch rather than an import at the top of this file: webpack replaces
      // process.env.NODE_ENV with a literal before parsing, so a dead branch is
      // never walked and the module never enters the production graph. A static
      // import would keep it — and its stylesheet — in the bundle even with the
      // route itself eliminated, which is the failure this shape avoids. In
      // production the wildcard below answers /motion-lab like any other unknown
      // URL. Delete this entry with the directory.
      ...(process.env.NODE_ENV !== "production"
        ? [
            {
              path: "/motion-lab",
              // eslint-disable-next-line global-require
              element: React.createElement(
                require("views/MotionLab/MotionLab.js").default
              ),
            },
          ]
        : []),
      // Anything unmatched, including "/". Everything real must be declared
      // above this line or it is silently answered with a redirect.
      { path: "*", element: toLanding },
    ],
  },
];
